import { Controller } from "stimulus"

export default class extends Controller {
  static get targets() {
    return ["results", "sortDir", "counter",
            "statusTrigger", "statusPanel", "statusLabel", "statusInput",
            "sortTrigger", "sortPanel", "sortLabel"]
  }

  connect() {
    const filterForm = this.element.querySelector("form")
    if (filterForm) filterForm.addEventListener("submit", e => e.preventDefault())

    // Close both dropdowns when clicking outside
    this.outsideClickHandler = (e) => {
      if (this.hasStatusPanelTarget && !this.statusTriggerTarget.contains(e.target) && !this.statusPanelTarget.contains(e.target)) {
        this.closePanel(this.statusPanelTarget)
      }
      if (this.hasSortPanelTarget && !this.sortTriggerTarget.contains(e.target) && !this.sortPanelTarget.contains(e.target)) {
        this.closePanel(this.sortPanelTarget)
      }
    }
    document.addEventListener("click", this.outsideClickHandler)

    // Re-sync UI when the user navigates back/forward through history.
    this.popstateHandler = () => this.syncFromUrl()
    window.addEventListener("popstate", this.popstateHandler)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler)
    window.removeEventListener("popstate", this.popstateHandler)
    if (this.currentRequest) this.currentRequest.abort()
  }

  // ── Generic panel helpers ────────────────────────────────────────────────────

  openPanel(panel) {
    panel.classList.remove("tw-opacity-0", "tw-scale-95", "tw-pointer-events-none")
    panel.classList.add("tw-opacity-100", "tw-scale-100")
  }

  closePanel(panel) {
    panel.classList.add("tw-opacity-0", "tw-scale-95", "tw-pointer-events-none")
    panel.classList.remove("tw-opacity-100", "tw-scale-100")
  }

  // ── Status dropdown ──────────────────────────────────────────────────────────

  toggleStatusPanel() {
    if (!this.hasStatusPanelTarget) return
    this.statusPanelTarget.classList.contains("tw-pointer-events-none")
      ? this.openPanel(this.statusPanelTarget)
      : this.closePanel(this.statusPanelTarget)
  }

  selectStatus(event) {
    const value = event.currentTarget.dataset.statusValue
    if (this.hasStatusInputTarget) this.statusInputTarget.value = value

    // Show badge in trigger when a status is selected; plain text for "All statuses"
    if (this.hasStatusLabelTarget) {
      if (value) {
        const badge = event.currentTarget.querySelector("span.tw-inline-flex")
        if (badge) this.statusLabelTarget.innerHTML = badge.outerHTML
      } else {
        this.statusLabelTarget.innerHTML = event.currentTarget.dataset.statusLabel
      }
    }

    this.closePanel(this.statusPanelTarget)
    this.load()
  }

  // ── Sort dropdown ────────────────────────────────────────────────────────────

  toggleSortPanel() {
    if (!this.hasSortPanelTarget) return
    this.sortPanelTarget.classList.contains("tw-pointer-events-none")
      ? this.openPanel(this.sortPanelTarget)
      : this.closePanel(this.sortPanelTarget)
  }

  selectSort(event) {
    const value = event.currentTarget.dataset.sortValue
    const label = event.currentTarget.dataset.sortLabel
    this.sortDirTarget.value = value
    if (this.hasSortLabelTarget) this.sortLabelTarget.textContent = label
    this.closePanel(this.sortPanelTarget)
    this.load()
  }

  // Intercepts pagy nav link clicks so desktop pagination doesn't cause a full page reload.
  paginate(event) {
    const paginationLink = event.target.closest("a")
    if (!paginationLink) return

    const pageNumber = new URL(paginationLink.href).searchParams.get("page")
    if (pageNumber) this.load({ pageNumber })
  }

  // Mirror dropdown selections back into the form/triggers from a URL — used by popstate.
  syncFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const status = params.get("status") || ""
    const sortDir = params.get("sort_dir") || "desc"

    if (this.hasStatusInputTarget) this.statusInputTarget.value = status
    if (this.hasStatusLabelTarget) {
      const matchingOption = this.statusPanelTarget?.querySelector(`[data-status-value="${CSS.escape(status)}"]`)
      if (status && matchingOption) {
        const badge = matchingOption.querySelector("span.tw-inline-flex")
        if (badge) this.statusLabelTarget.innerHTML = badge.outerHTML
      } else {
        this.statusLabelTarget.textContent = "All statuses"
      }
    }

    if (this.hasSortDirTarget) this.sortDirTarget.value = sortDir
    if (this.hasSortLabelTarget) {
      const matchingSort = this.sortPanelTarget?.querySelector(`[data-sort-value="${CSS.escape(sortDir)}"]`)
      if (matchingSort) this.sortLabelTarget.textContent = matchingSort.dataset.sortLabel
    }

    this.load({ pageNumber: params.get("page"), pushUrl: false })
  }

  load({ pageNumber = null, pushUrl = true } = {}) {
    const filterForm = this.element.querySelector("form")
    const requestUrl = new URL(filterForm.action, window.location.origin)
    const queryParams = new URLSearchParams(new FormData(filterForm))

    if (pageNumber) queryParams.set("page", pageNumber)

    requestUrl.search = queryParams.toString()

    if (pushUrl) history.pushState({}, "", requestUrl.toString())

    this.resultsTarget.classList.add("tw-opacity-50", "tw-pointer-events-none", "tw-transition-opacity")

    // Cancel any in-flight request so a slow earlier response can't overwrite a newer one.
    if (this.currentRequest) this.currentRequest.abort()
    const controller = new AbortController()
    this.currentRequest = controller

    window.fetch(requestUrl.toString(), { headers: { Accept: "text/html" }, signal: controller.signal })
      .then(response => response.text())
      .then(html => {
        const parsedDocument = new DOMParser().parseFromString(html, "text/html")
        const updatedResults = parsedDocument.querySelector("[data-payout-filters-target='results']")
        if (updatedResults) this.resultsTarget.innerHTML = updatedResults.innerHTML

        // Keep desktop pagination nav and result counter in sync with fresh HTML
        const updatedPagyNav = parsedDocument.querySelector(".js-pagy-nav")
        const currentPagyNav = this.element.querySelector(".js-pagy-nav")
        if (updatedPagyNav && currentPagyNav) currentPagyNav.innerHTML = updatedPagyNav.innerHTML

        const updatedCounter = parsedDocument.querySelector("[data-payout-filters-target='counter']")
        if (this.hasCounterTarget) {
          if (updatedCounter) {
            this.counterTarget.innerHTML = updatedCounter.innerHTML
            this.counterTarget.hidden = false
          } else {
            this.counterTarget.hidden = true
          }
        }

        // Re-initialize Bootstrap tooltips on any newly injected elements
        if (typeof $ !== "undefined") {
          $("[data-toggle='tooltip']", this.resultsTarget).tooltip({ html: true })
        }
      })
      .catch(err => {
        if (err.name === "AbortError") return
        console.error("[payout-filters]", err)
      })
      .finally(() => {
        if (this.currentRequest === controller) {
          this.currentRequest = null
          this.resultsTarget.classList.remove("tw-opacity-50", "tw-pointer-events-none")
        }
      })
  }
}
